import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Personal-account settings for the signed-in admin operator.
 * Distinct from the user-side "Settings" surface (theme, language,
 * vault PIN) — admins don't have a notes library so most of that is
 * irrelevant here.
 *
 * Three cards on a single page:
 *
 *   Profile — display name (editable). Email + role are read-only
 *             (email is identity; role is granted by a Superadmin).
 *
 *   Security — change password. Requires current password as
 *              proof-of-possession even though the caller is
 *              authenticated; a stolen JWT alone shouldn't be enough
 *              to rotate the password and lock out the real owner.
 *
 *   Account — read-only info card: created, last login + masked IP.
 *             Footer carries the sign-out CTA so it lives next to
 *             account context rather than orphaned at the bottom.
 *
 * Endpoints (added on the server in the same change):
 *   PUT /api/admin/auth/me/password  → AdminUserDto
 *   PUT /api/admin/auth/me/profile   → AdminUserDto
 *
 * @author <lukman@zshift.io>
 */
export function Settings() {
  const { me, updateMe, logout } = useAdminAuth();
  const { toast, confirm } = useUI();
  const navigate = useNavigate();

  const onSignOut = async () => {
    const ok = await confirm({
      title: 'Sign out of the admin console?',
      message: "You'll need to sign back in to manage accounts.",
      confirmLabel: 'Sign out',
      destructive: true,
    });
    if (!ok) return;
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-sub">
            Your console account. Changes here only affect <em>your</em> sign-in —
            other admins are managed from the <a href="/admins" onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/admins');
              window.dispatchEvent(new Event('popstate'));
            }} style={{ color: 'var(--purple)' }}>Admin Team</a> page.
          </p>
        </div>
      </div>

      <div className="row r-2-1">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ProfileCard me={me} onSaved={updateMe} toast={toast} />
          <PasswordCard toast={toast} />
        </div>
        <AccountInfoCard me={me} onSignOut={onSignOut} />
      </div>
    </div>
  );
}

// ── Profile card ─────────────────────────────────────────────────
function ProfileCard({ me, onSaved, toast }) {
  // Local mirror of the name field — only persists to the server on
  // submit. Initialised + reset whenever the canonical `me` changes
  // (e.g. another tab updates and the AuthContext re-fetches).
  const [name, setName] = useState(me?.name || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(me?.name || ''); }, [me?.name]);

  const dirty = (me?.name || '') !== name.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!dirty || busy) return;
    setBusy(true);
    try {
      const updated = await api('/api/admin/auth/me/profile', {
        method: 'PUT',
        body: { name: name.trim() },
      });
      onSaved(updated);
      toast.success('Name updated.');
    } catch (err) {
      toast.error(err?.message || 'Could not update your name.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-head">
        <h3>Profile</h3>
      </div>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={lbl}>Display name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jane Doe"
            maxLength={120}
            style={input}
          />
          <div style={hint}>Shown next to your actions in audit logs and on the Admin Team page.</div>
        </div>

        <div>
          <label style={lbl}>Email</label>
          <div style={readonly}>{me?.email || '—'}</div>
          <div style={hint}>Email is your identity and can't be changed here. Ask a Superadmin to re-invite under a new address.</div>
        </div>

        <div>
          <label style={lbl}>Role</label>
          <div>
            <span className={`pill role-${roleColor(me?.role)}`}>
              <span className="dot"/> {me?.roleLabel || me?.role || '—'}
            </span>
          </div>
          <div style={hint}>Only a Superadmin can change your role.</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            className="btn primary"
            disabled={!dirty || busy}
          >
            {busy ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Password card ────────────────────────────────────────────────
function PasswordCard({ toast }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  // Validation surfaces inline (red text under the relevant field)
  // rather than waiting for a server round-trip. Server still
  // validates the same rules — these are UX guardrails, not the
  // security check.
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit = !busy && current && next.length >= 8 && next === confirm && next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api('/api/admin/auth/me/password', {
        method: 'PUT',
        body: { currentPassword: current, newPassword: next },
      });
      toast.success('Password updated. Your existing session stays active.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      // 401 = wrong current password (most common). Show inline
      // rather than as a generic toast so the user knows which field
      // to fix.
      const isAuthErr = err?.status === 401;
      toast.error(isAuthErr
        ? 'Current password is incorrect.'
        : (err?.message || 'Could not change password.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-head">
        <h3>Change password</h3>
        <div className="sub">Min 8 characters · must differ from current</div>
      </div>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={lbl}>Current password</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            style={input}
          />
        </div>

        <div>
          <label style={lbl}>New password</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            aria-invalid={tooShort || sameAsCurrent}
            style={input}
          />
          {tooShort && <div style={errText}>Must be at least 8 characters.</div>}
          {sameAsCurrent && <div style={errText}>Must differ from your current password.</div>}
        </div>

        <div>
          <label style={lbl}>Confirm new password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            aria-invalid={mismatch}
            style={input}
          />
          {mismatch && <div style={errText}>Doesn't match the new password above.</div>}
        </div>

        <div style={tip}>
          <strong style={{ color: 'var(--ink-2)' }}>Tip · </strong>
          Use a password manager. The admin console has no "forgot password"
          flow — if you lose this password, another Superadmin has to delete
          + re-invite you.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            className="btn primary"
            disabled={!canSubmit}
          >
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Account info card (right rail) ───────────────────────────────
function AccountInfoCard({ me, onSignOut }) {
  return (
    <div className="card" style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h3 style={{ margin: 0 }}>Account</h3>
        <div className="sub" style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
          Read-only · audit context
        </div>
      </div>

      <InfoRow label="Joined">{me?.createdAt ? formatDate(me.createdAt) : '—'}</InfoRow>
      <InfoRow label="Last sign-in">
        {me?.lastLoginAt
          ? <>
              {agoString(me.lastLoginAt)}
              {me?.lastLoginIp && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  from {me.lastLoginIp}
                </div>
              )}
            </>
          : 'never'}
      </InfoRow>
      {me?.inviteNote && (
        <InfoRow label="Invite note">
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{me.inviteNote}</span>
        </InfoRow>
      )}

      <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: '4px 0' }} />

      <button
        type="button"
        className="btn danger"
        onClick={onSignOut}
        style={{ width: '100%', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <Icon name="lock" size={13} />
        Sign out
      </button>
    </div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div>
      <div style={{ ...lbl, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--ink)' }}>{children}</div>
    </div>
  );
}

// ── Shared inline styles (kept local — these are only used here) ─
const lbl = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ink-3)',
  marginBottom: 4,
  fontFamily: 'Geist Mono, monospace',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};
const input = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  color: 'var(--ink)',
  background: 'white',
  outline: 'none',
  fontFamily: 'inherit',
};
const readonly = {
  padding: '10px 12px',
  background: 'var(--bg-2)',
  borderRadius: 8,
  fontSize: 14,
  color: 'var(--ink-2)',
  fontFamily: 'Geist Mono, monospace',
};
const hint = {
  fontSize: 11.5,
  color: 'var(--ink-3)',
  marginTop: 4,
  lineHeight: 1.5,
};
const errText = {
  fontSize: 12,
  color: 'var(--red)',
  marginTop: 4,
};
const tip = {
  padding: 10,
  background: 'var(--bg-2)',
  borderRadius: 8,
  fontSize: 11.5,
  color: 'var(--ink-3)',
  lineHeight: 1.5,
};

// ── Helpers ──────────────────────────────────────────────────────
function roleColor(role) {
  if (role === 'SUPERADMIN')    return 'purple';
  if (role === 'SUPPORT_LEAD')  return 'blue';
  if (role === 'SUPPORT_AGENT') return 'green';
  return 'grey';
}

function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1)  return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24)    return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30)    return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}

function formatDate(iso) {
  const d = new Date(iso);
  if (!d.getTime()) return '—';
  // "12 Jun 2026"
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
